import React from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import ResourceFormDialog from './ResourceFormDialog';
import { CREATE_SITE, UPDATE_SITE } from '../graphql/mutations';
import { SITES_QUERY } from '../graphql/queries';

const SITE_QUERY = gql`
  query Site($id: ID!) {
    site(id: $id) {
      id
      name
      lat
      lon
    }
  }
`;

const FORM_FIELDS = [
  { key: 'name', label: 'Name', type: 'text', required: true },
  { key: 'lat', label: 'Latitude', type: 'number', inputType: 'text' },
  { key: 'lon', label: 'Longitude', type: 'number', inputType: 'text' }
];

/**
 * Create/edit site dialog (replaces the former /sites/new and
 * /sites/:id/edit form pages).
 *
 * @param {boolean} open
 * @param {{id: number}|null} site — record to edit, or null to create
 * @param {Function} onClose
 * @param {Array} [refetchQueries] — Apollo refetch list after save
 */
export default function SiteFormDialog({ open, site, onClose, refetchQueries }) {
  const id = site?.id ?? null;
  const { data, loading: resourceLoading } = useQuery(SITE_QUERY, {
    variables: { id },
    skip: !open || !id
  });

  const [updateSite, { loading: updateLoading }] = useMutation(UPDATE_SITE, {
    refetchQueries: refetchQueries ?? [{ query: SITES_QUERY }],
    awaitRefetchQueries: true
  });
  const [createSite, { loading: createLoading }] = useMutation(CREATE_SITE, {
    refetchQueries: refetchQueries ?? [{ query: SITES_QUERY }],
    awaitRefetchQueries: true
  });

  const handleSubmit = async (formState) => {
    try {
      const vars = {
        name: formState.name,
        lat: formState.lat ? parseFloat(formState.lat) : null,
        lon: formState.lon ? parseFloat(formState.lon) : null
      };
      if (id) {
        await updateSite({ variables: { input: { id, ...vars } } });
      } else {
        await createSite({ variables: { input: vars } });
      }
      onClose();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <ResourceFormDialog
      open={open}
      title={id ? 'Edit Site' : 'New Site'}
      resource={data?.site ?? null}
      fields={FORM_FIELDS}
      resourceLoading={Boolean(id) && resourceLoading}
      loading={updateLoading || createLoading}
      submitLabel={id ? 'Update Site' : 'Add Site'}
      onSubmit={handleSubmit}
      onClose={onClose}
    />
  );
}
